import {
  CreatePromotionEligibleCorridor,
  CreatePromotionPayload,
  PromotionAudience,
  PromotionItem,
  PromotionMinTransactionAmount,
  PromotionRewardConfig,
  PromotionStatus,
  PromotionType,
} from "../../../redux/type";
import { formatDateToYYYYMMDD } from "../../../utils/Helper";

/** Shared row shape for both "min transaction amount" and "reward" sections — both are simply a value per currency. */
export interface CurrencyValueRow {
  currency: string;
  value: string;
}

export interface CorridorValue {
  outbound: boolean;
  inbound: boolean;
}

export const CURRENCIES = ["USD", "GBP", "CAD", "EUR", "NGN"];

export const CAMPAIGN_TYPE_OPTIONS: { value: PromotionType; label: string }[] = [
  { value: "cashback_percent", label: "Cashback (%)" },
  { value: "cashback_flat", label: "Cashback (Flat Amount)" },
  { value: "fee_discount_percent", label: "Fee Discount (%)" },
  { value: "fee_discount_flat", label: "Fee Discount (Flat Amount)" },
];

export const PROMOTION_TYPE_LABELS: Record<PromotionType, string> = {
  fee_discount_percent: "Fee Discount %",
  fee_discount_flat: "Fee Discount (Flat)",
  cashback_percent: "Cashback %",
  cashback_flat: "Cashback (Flat)",
};

/** Whether a promotion's reward value is a percentage rather than a flat currency amount. */
export function isPercentType(type: PromotionType): boolean {
  return type === "cashback_percent" || type === "fee_discount_percent";
}

export const REGIONS_Code = [
  { value: "NG", label: "Nigeria" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "UK" },
  { value: "US", label: "USA" },
  { value: "EU", label: "Europe" },
];

/** Every corridor is NGN <-> this currency, so each is tied to the region that uses it. */
export const CORRIDOR_CURRENCIES: { code: string; region: string }[] = [
  { code: "USD", region: "US" },
  { code: "GBP", region: "GB" },
  { code: "CAD", region: "CA" },
  { code: "EUR", region: "EU" },
];

/** Nigeria is the fixed home side of every corridor (NGN <-> X), so it isn't tied to one specific corridor. */
const HOME_REGION = "NG";

export const AUDIENCE_OPTIONS: { value: PromotionAudience; label: string }[] = [
  { value: "all_users", label: "All Users" },
  { value: "new_users", label: "New Users" },
  { value: "old_users", label: "Existing Users" },
  { value: "influencer_referred_only", label: "Influencer-Referred Only" },
];

export function createEmptyCorridors(): Record<string, CorridorValue> {
  return Object.fromEntries(
    CORRIDOR_CURRENCIES.map(({ code }) => [code, { outbound: false, inbound: false }]),
  );
}

/**
 * Corridors selectable given the chosen eligible regions. Every corridor is
 * NGN <-> a foreign currency, so Nigeria is part of all of them — selecting
 * Nigeria (with or without other regions) always leaves every corridor
 * visible. Without Nigeria selected, a foreign region (US/GB/CA/EU) restricts
 * the list to just the corridor(s) for the foreign regions actually selected.
 */
export function visibleCorridorCurrencies(
  eligibleRegions: string[],
): { code: string; region: string }[] {
  if (eligibleRegions.length === 0 || eligibleRegions.includes(HOME_REGION)) {
    return CORRIDOR_CURRENCIES;
  }
  return CORRIDOR_CURRENCIES.filter(({ region }) => eligibleRegions.includes(region));
}

/** Picks the first currency not already used by an existing row, falling back to the first currency. */
export function nextAvailableCurrency(usedCurrencies: string[]): string {
  return (
    CURRENCIES.find((code) => !usedCurrencies.includes(code)) || CURRENCIES[0]
  );
}

/** Currencies selectable for a given row: everything not already used by another row, plus the row's own current currency. */
export function selectableCurrenciesFor(
  ownCurrency: string,
  usedCurrencies: string[],
): string[] {
  return CURRENCIES.filter(
    (code) => code === ownCurrency || !usedCurrencies.includes(code),
  );
}

/** Earliest selectable end date: the day after the start date (same-day is not allowed). */
export function getMinEndDate(startDate: string): string | undefined {
  if (!startDate) return undefined;
  const date = new Date(`${startDate}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return formatDateToYYYYMMDD(date);
}

export interface CampaignFormValues {
  campaignName: string;
  description: string;
  campaignType: PromotionType;
  minTransactionAmounts: CurrencyValueRow[];
  rewardConfigs: CurrencyValueRow[];
  eligibleRegions: string[];
  corridors: Record<string, CorridorValue>;
  usageLimitTotal: string;
  usageLimitPerUser: string;
  audience: PromotionAudience;
  startDate: string;
  endDate: string;
}

function buildCurrencyValueArray(rows: CurrencyValueRow[]) {
  return rows
    .filter((row) => row.value !== "")
    .map((row) => ({
      currency_code: row.currency,
      value: Number(row.value) || 0,
    }));
}

export function buildCampaignPayload(
  values: CampaignFormValues,
  status: PromotionStatus,
): CreatePromotionPayload {
  const eligibleCorridors: CreatePromotionEligibleCorridor[] = [];
  Object.entries(values.corridors).forEach(([code, value]) => {
    if (value.outbound) eligibleCorridors.push({ source: "NGN", destination: code });
    if (value.inbound) eligibleCorridors.push({ source: code, destination: "NGN" });
  });

  const isAllUsersAudience = values.audience === "all_users";

  const minTransactionAmount: PromotionMinTransactionAmount[] = buildCurrencyValueArray(
    values.minTransactionAmounts,
  );
  const rewards: PromotionRewardConfig[] = buildCurrencyValueArray(values.rewardConfigs);

  return {
    name: values.campaignName,
    description: values.description || null,
    type: values.campaignType,
    starts_at: values.startDate ? `${values.startDate}T00:00:00.000Z` : "",
    ends_at: values.endDate ? `${values.endDate}T00:00:00.000Z` : null,
    usage_limit_total:
      isAllUsersAudience || values.usageLimitTotal === ""
        ? null
        : Number(values.usageLimitTotal),
    usage_limit_per_user:
      values.usageLimitPerUser === "" ? null : Number(values.usageLimitPerUser),
    audience: values.audience,
    status,
    eligible_regions: values.eligibleRegions.length > 0 ? values.eligibleRegions : null,
    min_transaction_amount: minTransactionAmount.length > 0 ? minTransactionAmount : null,
    rewards: rewards.length > 0 ? rewards : null,
    eligible_corridors: eligibleCorridors.length > 0 ? eligibleCorridors : null,
  };
}

export function mapPromotionToFormValues(promotion: PromotionItem): CampaignFormValues {
  const corridors = createEmptyCorridors();
  (promotion.eligible_corridors || []).forEach((corridor) => {
    if (!corridor) return;
    if (corridor.source === "NGN" && corridors[corridor.destination]) {
      corridors[corridor.destination].outbound = true;
    } else if (corridor.destination === "NGN" && corridors[corridor.source]) {
      corridors[corridor.source].inbound = true;
    }
  });

  const minTransactionAmounts: CurrencyValueRow[] = (
    promotion.min_transaction_amount || []
  )
    .filter((entry) => entry?.currency_code)
    .map((entry) => ({
      currency: entry.currency_code,
      value: String(entry.value ?? ""),
    }));

  const rewardConfigs: CurrencyValueRow[] = (promotion.rewards || [])
    .filter((entry) => entry?.currency_code)
    .map((entry) => ({
      currency: entry.currency_code,
      value: String(entry.value ?? ""),
    }));

  return {
    campaignName: promotion.name,
    description: promotion.description || "",
    campaignType: promotion.type,
    minTransactionAmounts,
    rewardConfigs,
    eligibleRegions: promotion.eligible_regions || [],
    corridors,
    usageLimitTotal:
      promotion.usage_limit_total != null ? String(promotion.usage_limit_total) : "",
    usageLimitPerUser:
      promotion.usage_limit_per_user != null
        ? String(promotion.usage_limit_per_user)
        : "",
    audience: promotion.audience,
    startDate: promotion.starts_at ? promotion.starts_at.split("T")[0] : "",
    endDate: promotion.ends_at ? promotion.ends_at.split("T")[0] : "",
  };
}
